import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { Typography } from '@/components/ui/typography'

import * as S from './image-preview-modal.styled'

export interface ImagePreviewModalProps {
  onClose: VoidFunction
  images: string[]
  title: string
  initialIndex?: number
  alt?: string
}

export const ImagePreviewModal = ({
  onClose,
  images,
  title,
  initialIndex = 0,
  alt,
}: ImagePreviewModalProps) => {
  const theme = useTheme()
  const [index, setIndex] = useState(Math.min(Math.max(initialIndex, 0), images.length - 1))

  const hasMany = images.length > 1
  const showPrevious = () => setIndex((current) => (current - 1 + images.length) % images.length)
  const showNext = () => setIndex((current) => (current + 1) % images.length)

  useEffect(() => {
    if (!hasMany) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')
        setIndex((current) => (current - 1 + images.length) % images.length)
      if (e.key === 'ArrowRight') setIndex((current) => (current + 1) % images.length)
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hasMany, images.length])

  return (
    <Modal
      title={title}
      size="large"
      overlayStyle={{ zIndex: theme.zIndex.level5 }}
      onClose={onClose}
    >
      <S.Viewer>
        {hasMany && (
          <Button onlyBox use="secondary" aria-label="Previous image" onClick={showPrevious}>
            <Icon icon={CaretLeftIcon} />
          </Button>
        )}
        <S.Image src={images[index]} alt={alt ?? title} />
        {hasMany && (
          <Button onlyBox use="secondary" aria-label="Next image" onClick={showNext}>
            <Icon icon={CaretRightIcon} />
          </Button>
        )}
      </S.Viewer>
      {hasMany && (
        <S.Counter>
          <Typography size="text-medium" color="black500">
            {index + 1} / {images.length}
          </Typography>
        </S.Counter>
      )}
    </Modal>
  )
}
